/**
 * LeftShield Portal - Attestations Page
 * Manage suppression approvals and review audit trail.
 *
 * Route: /attestations | Nav: Compliance > Attestations
 *
 * Migrated onto the List & Grid Standard — see `.specify/journals/list-grid-adoption-journal.md`
 * (§ MIGRATION RECIPE) and the committed reference `src/pages/ApiKeysPage.tsx`.
 */
import React, { useEffect, useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  tokens,
  makeStyles,
  SelectTabEvent,
  SelectTabData,
} from '@fluentui/react-components';
import {
  ClipboardCheckmarkRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  HistoryRegular,
  WarningRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { suppressionApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { useAuth } from '../features/authentication';
import type { PendingSuppression, SuppressionAuditEntry } from '../types';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

type TabValue = 'pending' | 'audit';

/** Roles allowed to approve/reject suppression requests — mirrors
 *  `SuppressionPortalController.Approve` / `.Reject` `RequireAnyRole(...)`. */
const SUPPRESSION_REVIEWER_ROLES = ['Organization.Admin', 'TenantAdmin', 'SecurityAnalyst'];

/** Sortable columns per tab (only columns that carried a `compare` fn on the legacy DataGrid). */
const PENDING_SORT_FIELDS: readonly string[] = ['findingTitle', 'requestedAt'];
const AUDIT_SORT_FIELDS: readonly string[] = ['findingId', 'performedAt'];

const useStyles = makeStyles({
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

const getSeverityColor = (severity: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const getAuditActionColor = (action: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (action) {
    case 'Approved':
      return 'success';
    case 'Rejected':
      return 'danger';
    case 'Expired':
    case 'Revoked':
      return 'warning';
    case 'Requested':
      return 'informative';
    default:
      return 'informative';
  }
};

/** Wraps the (unpaged) `suppressionApi.getPending` response as a `PagedResult` —
 *  `useAdaptiveGridData` then applies search/sort/paging in memory (client mode). */
async function fetchPendingSuppressionsPage(
  params: ListQueryParams
): Promise<PagedResult<PendingSuppression>> {
  const items = await suppressionApi.getPending();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

/** Wraps the (unpaged) `suppressionApi.getAudit` response as a `PagedResult`. */
async function fetchSuppressionAuditPage(
  params: ListQueryParams
): Promise<PagedResult<SuppressionAuditEntry>> {
  const items = await suppressionApi.getAudit();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

export const AttestationsPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canReviewSuppressions = user?.roles?.some((role) => SUPPRESSION_REVIEWER_ROLES.includes(role)) ?? false;

  const [selectedTab, setSelectedTab] = useState<TabValue>('pending');
  const [confirmAction, setConfirmAction] = useState<{
    type: 'approve' | 'reject';
    suppression: PendingSuppression;
  } | null>(null);
  const [actionNotes, setActionNotes] = useState('');

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'requestedAt:desc' });

  const [sortFieldRaw, sortDirRaw] = (state.sort ?? 'requestedAt:desc').split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';

  // Each tab has its own valid sortable fields; fall back to that tab's own default when the
  // URL's sort field belongs to the OTHER tab (e.g. just switched tabs) — never sorts by a
  // field the active grid doesn't have.
  const pendingSortField = PENDING_SORT_FIELDS.includes(sortFieldRaw) ? sortFieldRaw : 'requestedAt';
  const auditSortField = AUDIT_SORT_FIELDS.includes(sortFieldRaw) ? sortFieldRaw : 'performedAt';
  const pendingSortState: SortState[] = [{ field: pendingSortField, dir: sortDir }];
  const auditSortState: SortState[] = [{ field: auditSortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortFieldRaw) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortFieldRaw) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const pendingGrid = useAdaptiveGridData<PendingSuppression>({
    queryKeyBase: ['attestations', 'pending'],
    fetchPage: fetchPendingSuppressionsPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['findingTitle', 'requestedBy', 'reason', 'justification'],
    sortState: pendingSortState,
  });

  // Lazily fetched — only once the Audit Trail tab is visited (preserves the original
  // `enabled: selectedTab === 'audit'` behaviour).
  const auditGrid = useAdaptiveGridData<SuppressionAuditEntry>({
    queryKeyBase: ['attestations', 'audit'],
    fetchPage: fetchSuppressionAuditPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['findingId', 'performedBy', 'action'],
    sortState: auditSortState,
    enabled: selectedTab === 'audit',
  });

  const activeGrid = selectedTab === 'pending' ? pendingGrid : auditGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a tab switch,
  // search, or approve/reject mutation.
  useEffect(() => {
    if (!activeGrid.isLoading && state.page > activeGrid.totalPages) {
      setPage(activeGrid.totalPages);
    }
  }, [activeGrid.isLoading, activeGrid.totalPages, state.page, setPage]);

  const approveMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      suppressionApi.approveSuppression(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attestations'] });
      setConfirmAction(null);
      setActionNotes('');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      suppressionApi.rejectSuppression(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attestations'] });
      setConfirmAction(null);
      setActionNotes('');
    },
  });

  const handleTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    const { type, suppression } = confirmAction;
    if (type === 'approve') {
      approveMutation.mutate({ id: suppression.id, notes: actionNotes || undefined });
    } else {
      rejectMutation.mutate({ id: suppression.id, notes: actionNotes || undefined });
    }
  };

  const pendingCount = pendingGrid.totalCount;
  const actionButtonCount = 2;
  const pendingColumnCount = canReviewSuppressions ? 7 : 6;
  const auditColumnCount = 5;

  let pendingBody: React.ReactNode;
  if (pendingGrid.isLoading) {
    pendingBody = <LoadingSkeleton rows={6} columns={pendingColumnCount} />;
  } else if (pendingGrid.isError) {
    pendingBody = <ErrorState message="Unable to load pending suppressions." onRetry={pendingGrid.refetch} />;
  } else if (pendingGrid.items.length === 0) {
    pendingBody = (
      <EmptyState
        icon={<ClipboardCheckmarkRegular />}
        title="No Pending Suppression Requests"
        description="All attestation requests have been processed."
      />
    );
  } else {
    pendingBody = (
      <ListLoadingOverlay busy={pendingGrid.isFetching && !pendingGrid.isLoading}>
        <Table aria-label="Pending suppression requests">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Severity</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'findingTitle' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('findingTitle')}
                onClick={() => toggleSort('findingTitle')}
                style={{ width: '100%' }}
              >
                Finding
              </TableHeaderCell>
              <TableHeaderCell>Reason</TableHeaderCell>
              <TableHeaderCell>Justification</TableHeaderCell>
              <TableHeaderCell>Requested By</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'requestedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('requestedAt')}
                onClick={() => toggleSort('requestedAt')}
              >
                Date
              </TableHeaderCell>
              {canReviewSuppressions && (
                <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                  Actions
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getSeverityColor(item.findingSeverity)}>
                    {item.findingSeverity}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.findingTitle}</Body1>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.reason}</Badge>
                </TableCell>
                <TableCell>
                  <Body2
                    style={{
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.justification}
                  </Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.requestedBy}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{new Date(item.requestedAt).toLocaleDateString()}</Body2>
                </TableCell>
                {canReviewSuppressions && (
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Button
                        appearance="primary"
                        size="small"
                        icon={<CheckmarkCircleRegular />}
                        onClick={() => setConfirmAction({ type: 'approve', suppression: item })}
                      >
                        Approve
                      </Button>
                      <Button
                        appearance="secondary"
                        size="small"
                        icon={<DismissCircleRegular />}
                        onClick={() => setConfirmAction({ type: 'reject', suppression: item })}
                      >
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  let auditBody: React.ReactNode;
  if (auditGrid.isLoading) {
    auditBody = <LoadingSkeleton rows={6} columns={auditColumnCount} />;
  } else if (auditGrid.isError) {
    auditBody = <ErrorState message="Unable to load the audit trail." onRetry={auditGrid.refetch} />;
  } else if (auditGrid.items.length === 0) {
    auditBody = (
      <EmptyState icon={<HistoryRegular />} title="No Audit Trail Entries" description="No audit trail entries found." />
    );
  } else {
    auditBody = (
      <ListLoadingOverlay busy={auditGrid.isFetching && !auditGrid.isLoading}>
        <Table aria-label="Suppression audit trail">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'findingId' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('findingId')}
                onClick={() => toggleSort('findingId')}
              >
                Finding ID
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100%' }}>Performed By</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortFieldRaw === 'performedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('performedAt')}
                onClick={() => toggleSort('performedAt')}
              >
                Date
              </TableHeaderCell>
              <TableHeaderCell>Notes</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {auditGrid.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getAuditActionColor(item.action)}>
                    {item.action}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body2 style={{ fontFamily: 'monospace' }}>{item.findingId}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.performedBy}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{new Date(item.performedAt).toLocaleString()}</Body2>
                </TableCell>
                <TableCell>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{item.notes || '-'}</Body2>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Attestations"
        subtitle="Suppression approvals, attestation review, and compliance exception management"
      />

      {/* Summary Cards — non-list content, stays outside the scrolling list frame. */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<ClipboardCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteYellowForeground1 }} />}
            header={<Body2>Pending</Body2>}
            description={
              <Title2 style={{ color: pendingCount > 0 ? tokens.colorPaletteYellowForeground1 : tokens.colorNeutralForeground1 }}>
                {pendingCount}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<CheckmarkCircleRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }} />}
            header={<Body2>Active</Body2>}
            description={<Title2>0</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<HistoryRegular style={{ fontSize: '1.5rem', color: tokens.colorNeutralForeground3 }} />}
            header={<Body2>Expired</Body2>}
            description={<Title2>0</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteYellowForeground1 }} />}
            header={<Body2>Expiring Soon</Body2>}
            description={<Title2 style={{ color: tokens.colorPaletteYellowForeground1 }}>0</Title2>}
          />
        </Card>
      </div>

      <div className={styles.listSection}>
        <ListPageLayout
          toolbar={
            <>
              <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
                <Tab value="pending" icon={<ClipboardCheckmarkRegular />}>
                  Pending Approvals
                  {pendingCount > 0 && (
                    <Badge appearance="filled" color="warning" style={{ marginLeft: tokens.spacingHorizontalS }}>
                      {pendingCount}
                    </Badge>
                  )}
                </Tab>
                <Tab value="audit" icon={<HistoryRegular />}>
                  Audit Trail
                </Tab>
              </TabList>
              <ListToolbar
                search={state.search}
                onSearchChange={setSearch}
                searchPlaceholder={
                  selectedTab === 'pending' ? 'Search pending suppressions…' : 'Search audit trail…'
                }
                onRefresh={activeGrid.refetch}
                isRefreshing={activeGrid.isFetching}
                testIdPrefix="attestations"
              />
            </>
          }
          footer={
            <Pager
              page={state.page}
              totalPages={activeGrid.totalPages}
              hasPreviousPage={activeGrid.hasPreviousPage}
              hasNextPage={activeGrid.hasNextPage}
              totalCount={activeGrid.totalCount}
              onPageChange={setPage}
              testId="attestations-pager"
            />
          }
        >
          {selectedTab === 'pending' ? pendingBody : auditBody}
        </ListPageLayout>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmAction !== null}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setConfirmAction(null);
            setActionNotes('');
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {confirmAction?.type === 'approve' ? 'Approve Suppression' : 'Reject Suppression'}
            </DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Body1>
                  {confirmAction?.type === 'approve'
                    ? 'Are you sure you want to approve this suppression request?'
                    : 'Are you sure you want to reject this suppression request?'}
                </Body1>
                {confirmAction?.suppression && (
                  <Card>
                    <div className={ps.cardBody}>
                      <div className={ps.inlineRow}>
                        <Badge appearance="filled" color={getSeverityColor(confirmAction.suppression.findingSeverity)}>
                          {confirmAction.suppression.findingSeverity}
                        </Badge>
                        <Body1 style={{ fontWeight: 500 }}>{confirmAction.suppression.findingTitle}</Body1>
                      </div>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        Reason: {confirmAction.suppression.reason}
                      </Body2>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                        Justification: {confirmAction.suppression.justification}
                      </Body2>
                    </div>
                  </Card>
                )}
                <Field label="Notes (optional)">
                  <Input
                    value={actionNotes}
                    onChange={(_, data) => setActionNotes(data.value)}
                    placeholder="Add a note for the audit trail..."
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setConfirmAction(null);
                  setActionNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={confirmAction?.type === 'approve' ? <CheckmarkCircleRegular /> : <DismissCircleRegular />}
                onClick={handleConfirm}
                disabled={approveMutation.isPending || rejectMutation.isPending}
              >
                {approveMutation.isPending || rejectMutation.isPending
                  ? 'Processing...'
                  : confirmAction?.type === 'approve'
                  ? 'Approve'
                  : 'Reject'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
